import { Controller } from '@hotwired/stimulus';
import { get } from '@rails/request.js';

/**
 * Task suggestions - a listbox under the task input with the tasks already used on the selected project.
 * Values:  url (task suggestions endpoint) | create (Boolean, adds "Create task … and start" that submits the form) |
 *          createLabel ("Create task “%<task>s” and start") | heading ("Tasks in %<project>s") | totalLabel ("%<total>s so far")
 * Targets: input (text input, role=combobox) | list (role=listbox) | project (the project select)
 * @example <form data-controller="task-suggestions" data-task-suggestions-url-value="/task_suggestions">
 *            <select data-task-suggestions-target="project" data-action="task-suggestions#projectChanged">
 *            <input data-task-suggestions-target="input" data-action="input->task-suggestions#search focus->task-suggestions#search keydown->task-suggestions#keydown blur->task-suggestions#hide">
 *            <div role="listbox" hidden data-task-suggestions-target="list"></div>
 */
export default class extends Controller {
  static targets = ['input', 'list', 'project'];
  static values = {
    url: String,
    create: { type: Boolean, default: false },
    createLabel: { type: String, default: '%<task>s' },
    heading: { type: String, default: '%<project>s' },
    totalLabel: { type: String, default: '%<total>s' },
    delay: { type: Number, default: 180 }
  };

  connect () {
    this.suggestions = [];
    this.activeIndex = -1;
    this.listTarget.addEventListener('mousedown', (event) => event.preventDefault());
  }

  disconnect () {
    clearTimeout(this.timeout);
  }

  search () {
    clearTimeout(this.timeout);
    this.timeout = setTimeout(() => this.#fetch(), this.delayValue);
  }

  projectChanged () {
    this.suggestions = [];
    this.hide();
  }

  keydown (event) {
    if (this.listTarget.hidden) {
      if (event.key === 'ArrowDown') this.search();
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.#activate(this.activeIndex + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.#activate(this.activeIndex - 1);
        break;
      case 'Enter':
        if (this.activeIndex >= 0) {
          event.preventDefault();
          this.#options()[this.activeIndex].click();
        }
        break;
      case 'Escape':
        event.preventDefault();
        this.hide();
        break;
      default:
    }
  }

  hide () {
    this.listTarget.hidden = true;
    this.listTarget.innerHTML = '';
    this.activeIndex = -1;
    this.inputTarget.setAttribute('aria-expanded', 'false');
    this.inputTarget.removeAttribute('aria-activedescendant');
  }

  pick (event) {
    this.inputTarget.value = event.currentTarget.dataset.task;
    this.hide();
    this.inputTarget.focus();
  }

  create () {
    this.hide();
    this.element.requestSubmit();
  }

  async #fetch () {
    if (!this.hasProjectTarget || !this.projectTarget.value) return;

    const query = this.inputTarget.value.trim();
    const params = new URLSearchParams({ project_id: this.projectTarget.value, q: query });
    const separator = this.urlValue.includes('?') ? '&' : '?';
    const response = await get(`${this.urlValue}${separator}${params}`, { responseKind: 'json' });
    if (!response.ok || document.activeElement !== this.inputTarget) return;

    this.suggestions = await response.json;
    this.#render(query);
  }

  #render (query) {
    const exact = this.suggestions.some((item) => item.task.toLowerCase() === query.toLowerCase());
    const canCreate = this.createValue && query.length > 0 && !exact;
    if (this.suggestions.length === 0 && !canCreate) {
      this.hide();
      return;
    }

    const id = `${this.inputTarget.id}-option`;
    this.listTarget.innerHTML = '';
    if (this.suggestions.length > 0) {
      const heading = document.createElement('span');
      heading.className = 'suggestions__heading';
      heading.textContent = this.headingValue.replace('%<project>s', this.#projectName());
      this.listTarget.appendChild(heading);
    }
    this.suggestions.forEach((item, index) => {
      const option = this.#option(`${id}-${index}`, 'suggestions__item');
      option.dataset.task = item.task;
      option.dataset.action = 'task-suggestions#pick';
      const name = document.createElement('span');
      name.className = 'suggestions__task';
      name.textContent = item.task;
      const total = document.createElement('span');
      total.className = 'suggestions__total';
      total.textContent = this.totalLabelValue.replace('%<total>s', item.total);
      option.append(name, total);
      this.listTarget.appendChild(option);
    });
    if (canCreate) {
      const option = this.#option(`${id}-create`, 'suggestions__create');
      option.dataset.action = 'task-suggestions#create';
      option.textContent = this.createLabelValue.replace('%<task>s', query);
      this.listTarget.appendChild(option);
    }

    this.activeIndex = -1;
    this.listTarget.hidden = false;
    this.inputTarget.setAttribute('aria-expanded', 'true');
  }

  #option (id, className) {
    const option = document.createElement('button');
    option.type = 'button';
    option.id = id;
    option.className = className;
    option.setAttribute('role', 'option');
    option.setAttribute('aria-selected', 'false');
    return option;
  }

  #options () {
    return Array.from(this.listTarget.querySelectorAll('[role="option"]'));
  }

  #activate (index) {
    const options = this.#options();
    if (options.length === 0) return;

    this.activeIndex = (index + options.length) % options.length;
    options.forEach((option, i) => {
      const active = i === this.activeIndex;
      option.classList.toggle('suggestions__item--active', active);
      option.setAttribute('aria-selected', String(active));
    });
    this.inputTarget.setAttribute('aria-activedescendant', options[this.activeIndex].id);
  }

  #projectName () {
    const selected = this.projectTarget.selectedOptions[0];
    return selected ? selected.textContent.trim() : '';
  }
}
